import { TLatori, TChannelPair, IModule, SubModule, IDevice, IRoled, Roled,IModuleController,IModuleDefController,TItemChannel,TStatusPair,TChanExcl } from "./model"
import { Div, applyLum, applySat, TStyle, TKlass, Span} from "./html"
import { IMatrixCellItemController, IDragged} from './logic'
import { IMidiLearn, TMidiLearnState, MidiNote, IMidiNote } from "./models/midi"
import { TriPop, ContextPop, IMenuChoice } from './menu'
import { Events } from "./event"
import { useRef } from "react"
import { IArch, ChannelPort } from "./arch"

const D = Div
const L = console.log

// XXX put this someplace shared
const onClick = handler => (handler && {
    onClick: e => {
        // ONLY plain clicks!
        e.button === 0 && handler && handler(e)
        e.stopPropagation()
        e.preventDefault()
    },
    // PREVENT double click-thru
    onDoubleClick: e => {
        e.stopPropagation()
        e.preventDefault()
    }
})

export interface IMatrixStubCell extends IRoled {
    phonic?: TLatori
    stub: true
}

export interface IMatrixModuleCell extends IRoled {
    id: string
    name: string
    midi?: IMidiLearn
    dragging: boolean
    device: IDevice
    channels: {L: TChannelPair} | {L: TChannelPair, R: TChannelPair}
    phonic?: TLatori
    overlay?: {
        arch: IArch
    }
}

interface IChainModuleArgs {
    module?: IModule
    invalid?: boolean
    cell: IMatrixStubCell | IMatrixModuleCell
    select?: boolean
    side?: TChanExcl
    top?: boolean
    stereo?: boolean
    m2s?: boolean
    bottom?: boolean
    split: boolean
    grouped?: {
        index: number
        count: number
        top?: boolean
        bottom?: boolean
        abFocus?: boolean
        replaceTarget?: boolean
    }
    conjoined?: boolean
    controllers?: {
        cell?: IMatrixCellItemController
        module?: IModuleController
    }
    dragSeer?: {
        stop: () => void
        start: (dragged:IDragged) => void
        item: IDragged
    }
    bypassable: boolean
    active: TStatusPair
    bypassed: TStatusPair
    ineligible?: boolean
    proc?: boolean
    color?: string
    channel?: TItemChannel
    midi?: {
        tab?: {
            click: () => void
            state: TMidiLearnState
        }
        L: {
            click: () => void
            state: TMidiLearnState
        }
        R?: {
            click: () => void
            state: TMidiLearnState
        }
    }
}

interface IChainModuleChannelArgsBase {
    module?: IModule
    cell: IMatrixModuleCell
    side?: TChanExcl
    split: boolean
    m2s?: boolean
    conjoined?: boolean
    controllers?: {
        cell?: IMatrixCellItemController
    }
    bypassable: boolean
    active: TStatusPair
    bypassed: TStatusPair
    color?: string
}

interface IChainModuleChannelArgs extends IChainModuleChannelArgsBase {
    midi?: {
        click: () => void
        state: TMidiLearnState
    }
}

interface IChainModuleChannelsArgs extends IChainModuleChannelArgsBase {
    midi?: {
        L: {
            click: () => void
            state: TMidiLearnState
        }
        R?: {
            click: () => void
            state: TMidiLearnState
        }
    }
}

function ChainModuleChannel (args:IChainModuleChannelArgs) {
    const {
        cell,
        midi,
        controllers,
        m2s,
        conjoined,
        color,
        side,
        split,
    } = args

    const dragging = 'dragging' in cell ? cell.dragging : null
    const control = controllers?.cell
    const bypassable = args.bypassable && !dragging

    const R = !!side?.R
    const active = R ? !!args.active.R : !!args.active.L
    const bypassed = R ? active && !!args.bypassed.R : active && !!args.bypassed.L
    const allActive = (!split || !!args.active.R) && !!args.active.L
    const name = m2s ? 'M2S'
        : (cell.phonic?.L || conjoined && side?.L)
        ? 'L'
        : (cell.phonic?.R || conjoined && side?.R)
        ? 'R'
        : 'M'

    const activate = 
        midi
        ? midi.click
        : (control?.activate && bypassable)
        ?  e => {
            // SHIFT+CLICK? (individual channel selection otherwise)
            const channel = !Events.shift(e) && split ? (R ? 'R' : 'L') : undefined
            control.activate!(channel,channel ? !active : !allActive)
        }
        : undefined

    const chanStyle = color && !bypassed ? NodeChannel.active(color) : {}

    // CHANNEL selection
    return <Div {...{
        ...(onClick(activate)),
        k: [
            midi && 'midi-learn',
            midi?.state === 'learning' && 'learning',
            'channel','acticon',
            !active && !midi && 'inactive',
            bypassed && 'bypassed',
            conjoined && 'stereo',
            conjoined && side?.L && 'L',
            m2s && 'm2s',
        ],
        s: !midi && active ? chanStyle : {},
        child: name,
    }}/>
}

function ChainModuleChannels (args:IChainModuleChannelsArgs) {
    const {
        conjoined,
        side,
        midi,
    } = args

    // DISPLAY channel selections RIGHT-side only for STEREO NODE
    if (side?.L && conjoined) {
        return <></>
    }
    else if (conjoined) {
        return <>
            <ChainModuleChannel {...{
                    ...args,
                    midi: midi?.L && {
                        click: midi.L.click,
                        state: midi.L.state,
                    },
                    side:{L:true}}}/>
            <ChainModuleChannel {...{
                    ...args,
                    midi: midi?.R && {
                        click: midi.R.click,
                        state: midi.R.state,
                    },
                    side:{R:true}}}/>
        </>
    }
    else {
        return <ChainModuleChannel {...{
            ...args,
            midi: midi?.L && {
                click: midi.L.click,
                state: midi.L.state,
            },
        }}/>
    }
}

function ChainModuleTab (
        {k,s,midi,group,onClick}
        : {
            k: TKlass
            s: TStyle
            midi: {
                state?: TMidiLearnState,
                control?: {
                    assignment?: IMidiNote
                    choices () : IMenuChoice[]
                }
            }
            group: string
            onClick: () => void
        }) {

    const ref = useRef<HTMLDivElement>(null)

    return (
        <D ref={ref} k={k} s={s} onClick={onClick}>
            <D k={['index',midi.state && 'midi-learn', midi.state === 'learning' && 'learning']}>
                {group}
            </D>
            {midi?.control?.choices && <ContextPop items={midi.control.choices}/> }
        </D>
    )
}

// Module as would appear in chain
export function ChainModule (args:IChainModuleArgs)
{
    const {
        module,
        midi,
        cell,
        conjoined,
        controllers,
        proc,
        side,
        channel,
        split,
    } = args

    const invalid = module?.invalid
    
    const stub = (cell as IMatrixStubCell).stub

    const ref = useRef<HTMLDivElement>(null)

    function indexToChar (index) {
        if (typeof index !== 'number') return index
        // character code offset
        const off = 
          index < 26 ? 65 // A-Z
          : index < 52 ? 71 // a-z
          : index < 62 ? -4 // 0-9
          : -1
      
        if (off < 0) throw new Error(`AB-group index ${index} out of range`)
        return String.fromCharCode(index + off)
    }

    /*
    const dragged:IDragged|undefined = module && dragSeer
        ? {
            chain: dragSeer.item.chain,
            module,
            role: Roled.create(module),
        }
        : undefined

    // IS NODE BEING DRAGGED?
    const [{dragging:$dragging},$drag,preview] = useDrag(() => ({
        type: 'module',
        item: () => dragged,
        end: dragSeer?.stop,
        collect: monitor => ({ dragging: monitor.isDragging() }),
    }),[ref,cell])

    // CAPTURE drag state
    const state = useRef($dragging)
    if (!state.current && $dragging && dragged) {
        dragSeer?.start(dragged)
    }
    state.current = $dragging

    if (dragSeer) {
        // INVISIBLE DRAG SOURCE: hides default drag preview (i.e. ghost)
        preview(getEmptyImage(),{ captureDraggingState: true })
        $drag(ref)
    }
        */

    const control = controllers?.cell
    const modname = 'name' in cell ? cell.name : null
    const overlay = 'overlay' in cell ? cell.overlay : null
    const dragging = 'dragging' in cell ? cell.dragging : null
    const device = 'device' in cell ? cell.device : null
    const {ineligible} = args
    const grouped = !dragging ? args.grouped : undefined
    const bypassable = args.bypassable && !dragging
    const allActive = (!split || !!args.active.R) && !!args.active.L
    const tab = {
        activate:
            midi?.tab
            ? midi.tab.click
            : (control?.activate && bypassable)
            ? () => control.activate!(undefined,!allActive)
            : undefined,
        active: !!args.active.R || !!args.active.L,
        bypassed: (!!args.active.R || !!args.active.L) && (!!args.bypassed.R || !!args.bypassed.L),
    }

    const activate = midi?.L
        ? midi?.L.click
        : (control?.activate && bypassable)
        ? () => control.activate!(undefined,!allActive)
        : undefined

    // SPANS + METADATA
    const $ = {
        device: { k: ['device',proc && 'proc'], child: device?.name},
        name: { k: ['name',proc && 'proc'], child: modname },
    }

    return (
        <Div
            ref={ref}
            onClickCapture={e => {
                if (e.button === 0 && Events.cmd(e) && control?.delete) {
                    control.delete(channel ?? 'L')
                    e.stopPropagation()
                    e.preventDefault()
                }
                // ALT+CLICK: copy this node into a fresh chain
                else if (e.button === 0 && Events.alt(e) && control?.isolate) {
                    control.isolate(channel ?? 'L')
                    e.stopPropagation()
                    e.preventDefault()
                }
            }}
            k={[
                'chain-module',
                control?.delete && 'deletable',
                control?.isolate && 'isolatable',
                overlay && 'overlay',
                args.m2s && 'm2s',
                args.select && 'selected',
                conjoined && 'conjoined',
                side?.L ? 'left' : side?.R && 'right',
                grouped?.top ? 'top' : grouped?.bottom && 'bottom',
                grouped && 'grouped',
                grouped?.abFocus && 'ab-focus',
                grouped?.replaceTarget && 'replace-target',
                ineligible && 'ineligible',
                invalid && 'invalid',
                stub && 'stub',
                dragging 
                    ? 'dragging'
                    // role specific styling - ignore when dragging
                    : cell.proc ? 'proc' : cell.source ? 'source' : cell.dest ? 'dest' : 'none',
            ]}>
            {module
                && overlay
                && (!conjoined || side?.R) 
                && <PortOverlay module={module} arch={overlay.arch}/>
            }
            {!dragging && !side?.R && grouped && 
                <ChainModuleTab
                    k={['tab',tab.active && 'active',tab.bypassed && 'bypassed']}
                    group={indexToChar(grouped.index)}
                    midi={{
                        state: midi?.tab?.state,
                        control: control?.midi,
                    }}
                    {...onClick(tab.activate)}
                />
            }
            <Div k={['frame',side?.L && 'left',side?.R && 'right']}>
                {stub 
                    ? <Div k="stub">Drag a {cell.source ? 'Source' : cell.dest ? 'Destination' : 'Processor'} here</Div>
                    : <>
                        {!(conjoined && side?.R) &&
                            <D {...onClick(activate)}>
                                <Div {...$.device}/>
                                <Div {...$.name}/>
                            </D>
                        }
                        {
                            // DISPLAY channel selections
                            !stub && (side?.L && conjoined
                                ? <></>
                                : <>
                                <D k='buffer' {...onClick(activate)}/>
                                    <ChainModuleChannels {...{
                                        ...args,
                                        midi,
                                        cell:cell as IMatrixModuleCell,
                                    }}/>
                                </>
                            )
                        }
                        {!dragging && !(conjoined && side?.L) &&
                            <TriPop
                                items={[
                                    control?.mult && {
                                        label:'Mult',
                                        choose: control.mult,
                                    },
                                    control?.ab && {
                                        label:'AB',
                                        choose: control.ab,
                                    },
                                    control?.split && {
                                        label:'Dual Mono',
                                        choose: control.split,
                                    },
                                    control?.bridge && {
                                        label:'Bridge',
                                        choose: () => control.bridge && control.bridge(channel ?? 'L'),
                                    },
                                    control?.unbridge && {
                                        label:'Unbridge',
                                        choose: control.unbridge,
                                    },
                                    control?.ungroup && {
                                        label:'Ungroup',
                                        choose: control.ungroup,
                                    },
                                    controllers?.module && {
                                        label: `MIDI ${
                                            controllers.module.midi.assignment
                                            ? `${MidiNote.toString(controllers.module.midi.assignment)}`
                                            : ''
                                        }`,
                                        divider: 'top',
                                        choices: controllers.module.midi.choices,
                                    },
                                    controllers?.module && {
                                        label:'Go to device...',
                                        divider: 'top',
                                        choose: () => controllers?.module && controllers.module.gotoDevice(),
                                    },
                                    control?.delete && {
                                        divider: 'top',
                                        label:'Delete',
                                        choose: () => control.delete && control.delete(channel ?? 'L'),
                                    },
                                ]}
                            />
                        }
                    </>
                }
            </Div>
        </Div>
    )
}

export function ChainBlank ()
{
    return <Div k={['chain-blank']}/>
}

/** 
 * UTILTIES for rendering node channels
 */
class NodeChannel {
    /**
     * ACTIVE (in-use) color scheme to be used for channel styling
     * @param color 
     * @returns 
     */
    static active (color:string) {
        return {
            backgroundColor: applyLum(color,0.95),
            color: applySat(applyLum(color,0.25),0.8),
        }
    }
}

export function PortOverlay ({
    module,
    arch,
} : {
    module: IModule
    arch: IArch
})
{
    // EXTRACT module ports in form suitable for rendering
    const ins = module.channels
        .filter(({from: output}) => output)
        .map(({port}) => ChannelPort.toPortId(port,arch) ?? '❌')
    const outs = module.channels
        .filter(({to: input}) => input)
        .map(({port}) => ChannelPort.toPortId(port,arch) ?? '❌')
    const {phonia:{stereo,m2s},proc,dest} = module

    const phonic: TLatori | undefined = 
        SubModule.getPlace(module) === 'L'
        ? {L: true}
        : SubModule.getPlace(module) === 'R'
        ? {R: true}
        : undefined

    return <D k={['portid',proc ? 'proc' : dest ? 'dest' : 'source']}>{
        proc
            ? <>
                {m2s
                ?
                <>
                    <Div k='out'>{outs[0]}</Div>
                    → (
                        <Div k='in'>{ins[0]}</Div>
                        <D>|</D>
                        <Div k='in'>{ins[1]}</Div>
                    )
                </>
                :
                stereo 
                ?
                <>
                    <Div k='out'>{outs[0]}</Div> → <Div k='in'>{ins[0]}</Div>
                    <D>|</D>
                    <Div k='out'>{outs[1]}</Div> → <Div k='in'>{ins[1]}</Div>
                </>
                : phonic?.R
                ? <><Div k='out'>{outs[1]}</Div> → <Div k='in'>{ins[1]}</Div></>
                : <><Div k='out'>{outs[0]}</Div> → <Div k='in'>{ins[0]}</Div></>
            }
        </>
        : dest 
        ? <>
            {stereo 
            ? <><Div k='out'>{outs[0]}</Div><D>|</D><Div k='out'>{outs[1]}</Div></>
            : phonic?.R
            ? <Div k='out'>{outs[1]}</Div>
            : <Div k='out'>{outs[0]}</Div>
            }
        </>
        // SOURCE
        : <>
            {stereo 
            ? <><Div k='in'>{ins[0]}</Div><D>|</D><Div k='in'>{ins[1]}</Div></>
            : phonic?.R
            ? <Div k='in'>{ins[1]}</Div>
            : <Div k='in'>{ins[0]}</Div>
            }
        </>
    }</D>
} 

/**
 * Module as would appear in browser/navigator
 */
export function BrowserModule ({
        invalid,
        name,device,
        proc,dest,source,
        stereo,m2s,
        L,R,
        inuse,
        expanded,
        onExpand,
        onChooseChannel,
        setDevice,
        control,
        overlay,
    } : {
        invalid?: boolean
        name: string
        device: IDevice
        proc?: boolean
        dest?: boolean
        source?: boolean
        stereo?: boolean
        L?: boolean
        R?: boolean
        m2s?: boolean
        inuse: {
            L?: string
            R?: string
        }
        expanded?: boolean
        onExpand?: () => void
        // DOUBLE-CLICK an L/R chip to add just that channel to the selected chain
        onChooseChannel?: (channel:'L'|'R') => void
        setDevice: (id:string) => void
        control?: IModuleDefController
        overlay?: {
            module?: IModule
            arch: IArch
        }
     })
{
    const mono = (!L && !R && !stereo && !m2s)
    const inuseStyle = (color?:string) => color && NodeChannel.active(color)

    const used = {
        L: {
            k: inuse.L && 'used',
            s: inuseStyle(inuse.L),
        },
        R: {
            k: inuse.R && 'used',
            s: inuseStyle(inuse.R),
        },
    }

    // spans + metadata
    const $ = {
        expander: stereo && {
            k: ['expander','acticon'],
            ...onClick(onExpand),
            child: <span className='symbols'>{expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}</span>,
        },
        device: { k: 'device', child: device.name },
        name: { k: 'name', child: name },
        channels: {
            k: ['channels'],
            child: // getShape()
                stereo ? <>
                    <Div k={['channel','stereo','L',onChooseChannel && 'choosable',used.L.k]} s={{...used.L.s}}
                        onDoubleClick={onChooseChannel ? e => { e.stopPropagation(); onChooseChannel('L') } : undefined}>L</Div>
                    <Div k={['channel','stereo','R',onChooseChannel && 'choosable',used.R.k]} s={{...used.R.s}}
                        onDoubleClick={onChooseChannel ? e => { e.stopPropagation(); onChooseChannel('R') } : undefined}>R</Div>
                </>
                : m2s ? <><Div k={['channel','m2s',used.L.k]} s={{...used.L.s}}>M2S</Div></>
                : mono ? <><Div k={['channel',used.L.k]} s={{...used.L.s}}>M</Div></>
                : L ? <Div k={['channel',used.L.k]} s={{...used.L.s}}>L</Div>
                : <Div k={['channel',used.R.k]} s={{...used.R.s}}>R</Div>
        },
    }

    return (
        <Div 
            k={[
                'browser-module',
                overlay?.module && 'overlay',
                invalid && 'invalid',
                m2s && 'm2s',
                stereo && 'stereo',
                (inuse.L || inuse.R) && 'inuse',
                (L || R) && 'submodule',
                proc ? 'proc' : source ? 'source' : dest ? 'dest' : 'none',
            ]}>
                {overlay?.module && <PortOverlay module={overlay.module} arch={overlay.arch}/>}
                <Div {...$.expander}/>
                <Div {...$.device}/>
                <Div {...$.name}/>
                <Div {...$.channels}/>
                {control && 
                    <TriPop
                        items={
                            [
                                {
                                    label: 'Go to device...',
                                    choose: () => setDevice(device.id)
                                },
                            ]
                        }
                    />
                }
        </Div>
    )
}
